/**
 * Entry operations — create, update, archive.
 *
 * Every write into an `Entry` in this app arrives here and leaves through `patch.ts`. The vocabulary
 * each kind speaks — what a task's draft looks like, what a movie's does — is `entries/` business and
 * moves to a per-kind `write-spec.ts` in step 2; what lives here is the mechanics, which is one
 * implementation per container and verb.
 */

import {
  applyEntryPatch,
  valueForDocument,
  type EntryPatch,
  type MetadataPatch,
} from './patch'
import { setPersonLinks } from './links'
import { findOrCreateGenreTopics } from './topics'
import { newId } from '../ids'
import type { AppDocument, Entry, EntryType } from '../schema'

/**
 * Genre names to resolve into topics, scoped to an entry type.
 *
 * Resolved inside the same change as the write that uses them, so a created topic and the entry
 * pointing at it are one change rather than two.
 */
export interface GenreScope {
  names: string[]
  scope: EntryType | 'album'
}

/** What a caller supplies to create an entry. */
export interface EntryDraft {
  type: EntryType | string
  title: string
  content: string
  summary?: string
  imageUrl?: string
  url?: string
  metadata?: MetadataPatch
  topicIds?: string[]
  genres?: GenreScope
  projectId?: string | null
  areaId?: string | null
  peopleIds?: string[]
  /** Overrides the created and updated timestamps. For an import or a backfill, not for a user write. */
  createdAt?: string
}

/** What a caller supplies to change an entry. */
export interface EntryChange extends EntryPatch {
  genres?: GenreScope
  peopleIds?: string[]
}

/**
 * Create an entry, and its person links and genre topics with it.
 *
 * Returns the new entry's id, minted here — every id stored in the document comes from
 * `document/ids.ts` (#293). Reading the entry back is the caller's business: an entry handed out of
 * a change transaction is a live proxy that does not survive it.
 */
export function createEntry(doc: AppDocument, draft: EntryDraft): string {
  const id = newId()
  const now = draft.createdAt ?? new Date().toISOString()

  const topicIds = draft.genres
    ? findOrCreateGenreTopics(doc, draft.genres.names, draft.genres.scope)
    : (draft.topicIds ?? [])

  doc.entries[id] = valueForDocument({
    id,
    type: draft.type as EntryType,
    title: draft.title,
    content: draft.content,
    summary: draft.summary,
    imageUrl: draft.imageUrl,
    url: draft.url,
    metadata: draft.metadata ?? {},
    topicIds,
    projectId: draft.projectId ?? null,
    areaId: draft.areaId ?? null,
    createdAt: now,
    updatedAt: now,
  } as Entry)

  if (draft.peopleIds?.length) {
    setPersonLinks(doc, id, draft.peopleIds)
  }

  return id
}

/**
 * Change an entry, and stamp `updatedAt`.
 *
 * Returns `false` when there is no such entry. A missing entry is not an error here because the two
 * populations disagree about it: the mutation hooks throw a labelled error, and the components that
 * write from a card or a panel return silently. Reporting rather than deciding lets both keep the
 * behaviour they had.
 */
export function updateEntry(
  doc: AppDocument,
  id: string,
  change: EntryChange,
): boolean {
  const entry = doc.entries[id]
  if (!entry) return false

  const { genres, peopleIds, ...patch } = change

  if (genres) {
    patch.topicIds = findOrCreateGenreTopics(doc, genres.names, genres.scope)
  }

  applyEntryPatch(entry, patch)

  if (peopleIds !== undefined) {
    setPersonLinks(doc, id, peopleIds)
  }

  entry.updatedAt = new Date().toISOString()

  return true
}

/**
 * Archive an entry — what every "delete" in this app does.
 *
 * Does not stamp `updatedAt`: a caller that wants both passes `{ archivedAt }` to `updateEntry`.
 *
 * Returns `false` when there is no such entry, for the reason `updateEntry` does.
 */
export function archiveEntry(doc: AppDocument, id: string): boolean {
  const entry = doc.entries[id]
  if (!entry) return false

  entry.archivedAt = new Date().toISOString()

  return true
}
